import { getSupabaseClient } from "../supabase/index.js";
import { getDrillById } from "../trainer/drills.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "./hub-scoped-cache.js";

export function isoWeekKey(now = new Date()) {
  const d = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
  );
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

export function pickWeeklyDrill(drills, now = new Date()) {
  const list = (drills || [])
    .filter((drill) => drill?.id && drill.isGlobal !== false)
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));
  if (!list.length) return null;
  const key = isoWeekKey(now);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return list[hash % list.length];
}

const HUB_WEEKLY_DRILL_CACHE_MS = 60000;
/** @type {Map<string, Promise<{ok: boolean, data?: object|null, error?: any}>>} */
const __hubWeeklyDrillInflight = new Map();
/** @type {Map<string, { week: string, ts: number, value: {ok: boolean, data?: object|null, error?: any} }>} */
const __hubWeeklyDrillCache = new Map();

export function clearHubWeeklyDrillCache() {
  __hubWeeklyDrillCache.clear();
  __hubWeeklyDrillInflight.clear();
}

registerHubCacheClear(clearHubWeeklyDrillCache);

/**
 * Lädt nur IDs der Global-Drills, wählt die Wochenübung und reichert genau diese an.
 * @param {Date} [now]
 * @returns {Promise<{ok: boolean, data?: object|null, error?: any}>}
 */
export async function loadHubWeeklyDrill(now = new Date()) {
  const week = isoWeekKey(now);
  const scope = await resolveHubReadScope();
  const cacheKey = `${scope.key}|${week}`;
  const ts = Date.now();
  const cached = __hubWeeklyDrillCache.get(cacheKey);
  if (
    cached &&
    cached.week === week &&
    ts - cached.ts < HUB_WEEKLY_DRILL_CACHE_MS
  ) {
    return cached.value;
  }
  const inflight = __hubWeeklyDrillInflight.get(cacheKey);
  if (inflight) return inflight;

  const started = { ...scope };
  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return { ok: false, error: "no-supabase" };

      const { data, error } = await supabase
        .from("trainer_drills")
        .select("id")
        .eq("is_global", true);
      if (error) return { ok: false, error };

      const picked = pickWeeklyDrill(
        (data || []).map((row) => ({ id: row.id, isGlobal: true })),
        now,
      );
      if (!picked?.id) return { ok: true, data: null };

      return getDrillById(picked.id);
    } catch (e) {
      return { ok: false, error: e };
    }
  })();

  const promise = run
    .then((value) => {
      if (value?.ok && isHubReadScopeCurrent(started)) {
        __hubWeeklyDrillCache.set(cacheKey, {
          week,
          ts: Date.now(),
          value,
        });
      }
      return value;
    })
    .finally(() => {
      if (__hubWeeklyDrillInflight.get(cacheKey) === promise) {
        __hubWeeklyDrillInflight.delete(cacheKey);
      }
    });
  __hubWeeklyDrillInflight.set(cacheKey, promise);
  return promise;
}
